import { AuthBaseClient } from '../base-client';
import type {
  UserResponse,
  CreateUserRequest,
  UpdateUserRequest,
  UserPermissionsResponse,
} from './types';
import type { SearchRequest, SearchResponse } from '@vagusx/shared';

/**
 * Client for user API endpoints.
 */
export class UserClient extends AuthBaseClient {
  /**
   * Get current authenticated user.
   */
  async getMe(): Promise<UserResponse> {
    return this.get<UserResponse>('/api/users/me');
  }

  /**
   * Get current user's resolved permissions (UX-only).
   */
  async getMyPermissions(): Promise<UserPermissionsResponse> {
    return this.get<UserPermissionsResponse>('/api/users/me/permissions');
  }

  /**
   * Get user by ID.
   */
  async getById(id: string): Promise<UserResponse> {
    return this.get<UserResponse>(`/api/users/${id}`);
  }

  /**
   * Search users with pagination.
   */
  async search(request: SearchRequest): Promise<SearchResponse<UserResponse>> {
    return this.post<SearchResponse<UserResponse>>('/api/users/search', request);
  }

  /**
   * Create a new user.
   */
  async create(data: CreateUserRequest): Promise<UserResponse> {
    return this.post<UserResponse>('/api/users', data);
  }

  /**
   * Update a user.
   */
  async update(id: string, data: UpdateUserRequest): Promise<UserResponse> {
    return this.patch<UserResponse>(`/api/users/${id}`, data);
  }

  /**
   * List inactive (deactivated) users.
   */
  async listInactive(): Promise<UserResponse[]> {
    return this.get<UserResponse[]>('/api/users/inactive');
  }

  /**
   * Delete a user (soft delete).
   */
  async deleteUser(id: string): Promise<void> {
    return this.delete<void>(`/api/users/${id}`);
  }

  /**
   * Reactivate a deactivated user.
   */
  async reactivateUser(id: string): Promise<UserResponse> {
    return this.post<UserResponse>(`/api/users/${id}/reactivate`);
  }

  /**
   * Upload an avatar for the current user.
   */
  async uploadAvatar(file: File): Promise<UserResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return this.uploadFormData<UserResponse>('/api/users/me/avatar', formData);
  }

  /**
   * Remove the current user's avatar.
   */
  async removeAvatar(): Promise<UserResponse> {
    return this.delete<UserResponse>('/api/users/me/avatar');
  }
}
